import { Tooltip } from "flowbite-react";
import title from "src/calculator/title";
import { useFullscreen } from "src/hooks/useFullScreen";
import { useScenario } from "src/hooks/useScenario";
import { useUser } from "src/hooks/useUser";
import { Client, Income, ScenarioSettings, SelectedColumn } from "src/types";
import { getTaxRate, printNumber, taxTextColors, yearRange } from "src/utils";

interface Props {
  client: Client;
  selectedYear?: number;
  selectedColumn?: SelectedColumn;
  setSelectedColumn?: any;
  setSelectedYear?: any;
  print?: boolean;
  columns: { name: string; key: string }[];
  data: {
    value: React.ReactElement;
    key: string;
    tooltip?: React.ReactElement;
    selectedClass?: string;
    normalClass?: string;
  }[][];
}
const MapTable = ({
  setSelectedYear,
  selectedYear,
  setSelectedColumn,
  print,
  selectedColumn,
  client,
  columns,
  data,
}: Props) => {
  const currentYear = new Date().getFullYear();
  const height = 100;
  const { isFullscreen } = useFullscreen();

  const setColumn = (name: string) => () => {
    console.log(name);
    selectedColumn?.type === name
      ? setSelectedColumn({ type: "none", id: 0 })
      : setSelectedColumn({ type: name, id: 0 });
  };
  const setRow = (year: number) => () => {
    selectedYear === year ? setSelectedYear(-1) : setSelectedYear(year);
  };

  if (data.length === 0) {
    return <div className="text-center text-gray-500"></div>;
  }

  return (
    <div className="flex justify-between flex-wrap">
      {[0, 1, 2, 3, 4].map((tableInd) => {
        return (
          currentYear + client.liveSettings.maxYearsShown >
            currentYear + tableInd * height && (
            <div className="w-full">
              <table className="border bg-white text-sm! w-full">
                <thead
                  className={`text-xs cursor-pointer print:static bg-[#F9FAFB] text-black font-medium text-left sticky z-50 print:border-transparent print:border-b-gray-500 print:border-2 border ${isFullscreen ? "top-[202px]" : !print && "top-[271px]"} border-separate`}
                >
                  <tr>
                    {columns.map(({ name, key }, i) => {
                      return (
                        <th
                          className={`${i === 0 || i === columns.length - 1 ? "px-5" : "px-2"} py-[0.45rem] font-medium text-black ${key === "year" ? "m-auto" : ""} ${
                            selectedColumn?.type === data[0][i].key
                              ? data[0][i].selectedClass || "bg-slate-200"
                              : data[0][i].normalClass || ""
                          }`}
                          onClick={setColumn(key)}
                        >
                          <div
                            className={`flex ${!["year", "age"].includes(key) ? "justify-end" : "justify-start"} items-center gap-2`}
                          >
                            {name}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody
                  className={`text-sm ${print && "text-[14px]!"} inconsolata`}
                >
                  {yearRange(
                    currentYear + tableInd * height,
                    Math.min(
                      currentYear + (tableInd + 1) * height - 1,
                      currentYear + client.liveSettings.maxYearsShown - 1,
                    ),
                  ).map((line, index) => {
                    const tableLine = data[index];
                    return (
                      <tr
                        className={`${index % 2 == 1 ? "bg-[#F9FAFB]" : "bg-white"} border-y border-[#EAECF0] hover:bg-slate-100`}
                        onClick={setRow(line)}
                      >
                        {tableLine.map((item, i) => {
                          const selected =
                            selectedYear == line ||
                            selectedColumn?.type === item.key;
                          return (
                            <td
                              className={`px-2 ${print ? "py-[0.2rem]" : "py-[0.45rem]"} text-[#475467] ${
                                selected
                                  ? item.selectedClass || "bg-slate-200"
                                  : item.normalClass || ""
                              } ${!!item.tooltip ? "cursor-pointer" : ""} ${i === 0 || i === tableLine.length - 1 ? "px-4" : "px-2"} `}
                            >
                              <div
                                className={`flex ${!["year", "age"].includes(item.key) ? "justify-end" : "justify-start"} items-center gap-2`}
                              >
                                <Tooltip
                                  content={item.tooltip}
                                  theme={{ target: "" }}
                                  style="light"
                                  arrow={false}
                                  hidden={!!item.tooltip}
                                  className={`border-2 border-main-orange ${!!item.tooltip ? "" : "hidden"} w-72`}
                                  placement="bottom"
                                >
                                  <div
                                    className={`flex ${!["year", "age"].includes(item.key) ? "w-[100px]" : "justify-start"} justify-end`}
                                  >
                                    {item.value}
                                  </div>
                                </Tooltip>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        );
      })}
    </div>
  );
};

export const IncomeTooltip = ({
  scenario,
  selectedIncomes,
  year,
  client,
  colored,
}: {
  scenario: ScenarioSettings;
  selectedIncomes: Income[];
  year: number;
  client: Client;
  colored?: boolean;
}) => {
  const { user } = useUser();
  if (!selectedIncomes.length) return null;
  const taxRate = getTaxRate(user, scenario, year);
  const { scenario: calculated } = useScenario();

  return (
    <>
      <div className="text-lg mb-2">{year}</div>
      {selectedIncomes.map((income) => {
        const incomeIndex = scenario.incomes.findIndex(
          (inc) => inc.id === income.id,
        );
        return (
          <>
            {calculated[year]?.incomes[income.id]?.amount != 0 && (
              <div className="flex justify-between gap-3">
                <div
                  className={`font-bold ${colored ? taxTextColors[income.taxType]?.replace("bg", "text") : ""}`}
                >
                  <span className="font-inter" style={{ fontFamily: "Inter" }}>
                    {title(scenario.incomes, client.people, incomeIndex)}:{" "}
                  </span>
                </div>
                {printNumber(
                  calculated[year]?.incomes[income.id]?.amount * (1 - taxRate),
                )}{" "}
              </div>
            )}
          </>
        );
      })}
    </>
  );
};

export default MapTable;
